import { ETelemetryTopOf, RequestTimelinePoint } from 'multirpc-sdk';
import { createSelector, weakMapMemoize } from 'reselect';
import { ChainID } from '@ankr.com/chains-list';
import { IChartData } from '@ankr.com/telemetry';

import { RootState } from 'store';
import { aggregateBlockchainsCountsByChainID } from 'modules/stats/utils/aggregateBlockchainsCountsByChainID';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { mapSharkChainToChainPath } from 'modules/chains/utils/mapSharkChainNameToChainPath';
import { mapStatsForRequestsWidget } from 'domains/statistics/store/utils/mapStatsForRequestsWidget';
import {
  selectAllChainsPaths,
  selectAllPathsByChainId,
  selectBlockchains,
  selectPublicBlockchains,
} from 'modules/chains/store/selectors';

import {
  BlockchainsCountsMap,
  CountriesCountsMap,
  UsageTimelineMap,
} from '../../types';
import {
  IFetchPrivateStatsParams,
  selectPrivateStats,
} from '../../actions/fetchPrivateStats';
import { getTopElementsById } from '../../utils/getTopElementsById';
import { sumBlockchainsCountsByChainPaths } from '../../utils/sumBlockchainsCountsByChainPaths';

const usageTimelineMapFallback: UsageTimelineMap = {};
const timelineFallback: RequestTimelinePoint[] = [];

export const selectPrivateAPIKeysCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.API_KEY,
    }),
);

export const selectPrivateAPIKeysCountsByTraffic = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.API_KEY_BY_TRAFFIC,
    }),
);

export const selectPrivateBlockchainsCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.BLOCKCHAIN,
    }),
);

export const selectPrivateBlockchainsCountsByTraffic = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.BLOCKCHAIN_BY_TRAFFIC,
    }),
);

export const selectPrivateCountriesCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.COUNTRY,
    }),
);

export const selectPrivateCountriesCountsByTraffic = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.COUNTRY_BY_TRAFFIC,
    }),
);

export const selectPrivateErrorsCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.ERROR,
    }),
);

export const selectIPsCounts = createSelector(selectPrivateStats, ({ tops }) =>
  getTopElementsById({
    tops,
    id: ETelemetryTopOf.IP,
  }),
);

export const selectIPsCountsByTraffic = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.IP_BY_TRAFFIC,
    }),
);

export const selectPrivateMethodsCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.METHOD,
    }),
);

export const selectPrivateMethodsCountsByTraffic = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.METHOD_BY_TRAFFIC,
    }),
);

export const selectMonthlyUsageCounts = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.MONTHLY_USAGE,
    }),
);

export const selectMonthlyUsageCountsByTraffic = createSelector(
  selectPrivateStats,
  ({ tops }) =>
    getTopElementsById({
      tops,
      id: ETelemetryTopOf.MONTHLY_USAGE_BY_TRAFFIC,
    }),
);

export const selectPrivateBlockchainsCountsMap = createSelector(
  selectPrivateBlockchainsCounts,
  blockchainCounts =>
    blockchainCounts.reduce<BlockchainsCountsMap>((map, { count, name }) => {
      map[mapSharkChainToChainPath(name)] = Number(count);

      return map;
    }, {}),
);

export const selectPrivateCountriesCountsMap = createSelector(
  selectPrivateCountriesCounts,
  countriesCounts =>
    countriesCounts.reduce<CountriesCountsMap>(
      (countryMap, { count, name: country }) => ({
        ...countryMap,
        [country]: { country, requests: Number(count) },
      }),
      {},
    ),
);

export const selectPrivateCountriesCountsByTrafficMap = createSelector(
  selectPrivateCountriesCountsByTraffic,
  countriesCounts =>
    countriesCounts.reduce<CountriesCountsMap>(
      (countryMap, { count, name: country }) => ({
        ...countryMap,
        [country]: { country, requests: Number(count) },
      }),
      {},
    ),
);

const selectPrivateRequestsUsageTimeline = createSelector(
  selectPrivateStats,
  ({ request_timelines }) => {
    const usageTimeline = request_timelines.find(
      ({ name }) => name === 'Usage',
    );

    return usageTimeline?.points ?? timelineFallback;
  },
);

const selectPrivateTrafficUsageTimeline = createSelector(
  selectPrivateStats,
  ({ traffic_timelines }) => {
    const usageTimeline = traffic_timelines?.find(
      ({ name }) => name === 'Usage',
    );

    return usageTimeline?.points ?? timelineFallback;
  },
);

const selectPrivateRequestsChartData = createSelector(
  selectPrivateRequestsUsageTimeline,
  usageTimeline =>
    usageTimeline.map<IChartData>(({ time, value }) => ({
      time: new Date(time),
      value,
    })),
);

const selectPrivateTrafficChartData = createSelector(
  selectPrivateTrafficUsageTimeline,
  usageTimeline =>
    usageTimeline.map<IChartData>(({ time, value }) => ({
      time: new Date(time),
      value,
    })),
);

export const selectMappedPrivateRequestsChartData = createSelector(
  selectPrivateRequestsChartData,
  (_state: RootState, statsParams: IFetchPrivateStatsParams) => statsParams,
  (data, { timeframe, timeframeShift }) => {
    return mapStatsForRequestsWidget({
      data,
      timeframe,
      timeframeShift,
    });
  },
);

export const selectMappedPrivateTrafficChartData = createSelector(
  selectPrivateTrafficChartData,
  (_state: RootState, statsParams: IFetchPrivateStatsParams) => statsParams,
  (data, { timeframe, timeframeShift }) => {
    return mapStatsForRequestsWidget({
      data,
      timeframe,
      timeframeShift,
    });
  },
);

export const selectPrivateUsageTimelineMap = createSelector(
  selectPrivateRequestsUsageTimeline,
  usageTimeline => {
    const usageTimelineMap = usageTimeline.reduce<UsageTimelineMap>(
      (history, point) => {
        const timestamp = new Date(point.time).getTime();

        return { ...history, [timestamp]: Number(point.value) };
      },
      {},
    );

    return usageTimelineMap ?? usageTimelineMapFallback;
  },
);

export const selectPrivateTotalRequests = createSelector(
  selectPrivateStats,
  ({ total }) => Number(total),
);

export const selectPrivateTotalBytes = createSelector(
  selectPrivateStats,
  ({ total_bytes }) => Number(total_bytes),
);

export const selectPrivateErrorsPercentage = createSelector(
  selectPrivateErrorsCounts,
  selectPrivateTotalRequests,
  (errorsCounts, totalRequests) => {
    const errorsCount = errorsCounts.reduce(
      (acc, { count }) => acc + Number(count),
      0,
    );

    return (errorsCount / totalRequests) * 100;
  },
);

export const selectPrivateBlockchainCountByChainID = createSelector(
  selectPrivateBlockchainsCounts,
  (state: RootState, _params: IFetchPrivateStatsParams, chainId: ChainID) => ({
    chainId,
    state,
  }),
  (blockchainsCounts, { chainId, state }) => {
    const chainPaths = selectAllPathsByChainId(state, chainId);

    return sumBlockchainsCountsByChainPaths({
      blockchainsCounts,
      chainPaths,
    });
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
    memoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPrivateAggregatedBlockchainsCountsByChainID = createSelector(
  selectBlockchains,
  selectPublicBlockchains,
  selectAllChainsPaths,
  selectPrivateBlockchainsCounts,
  // eslint-disable-next-line max-params
  (blockchains, formattedBlockchains, allChainsPaths, blockchainsCounts) =>
    aggregateBlockchainsCountsByChainID({
      allChainsPaths,
      blockchains,
      blockchainsCounts,
      formattedBlockchains,
    }),
);
